import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { FaInstagram, FaLinkedin, FaFacebook, FaMapMarkedAlt, FaMoneyBillWave, FaChartLine, FaBalanceScale, FaUserTie, FaKey, FaHandshake, FaChartPie, FaWhatsapp } from 'react-icons/fa';
import heroVideo from '../assets/videos/buy-hero.mp4';

import { propertiesForSale } from '../data/properties';
import { communities } from '../data/communities';

const featuredListings = propertiesForSale;
const popularCommunities = communities;

const investmentTips = [
  {
    title: 'Location Analysis',
    desc: 'Research emerging areas and infrastructure developments for long-term appreciation.',
    icon: <FaMapMarkedAlt className="w-12 h-12 text-accent mx-auto mb-4" />
  },
  {
    title: 'Payment Plans',
    desc: 'Explore developer payment plans that align with your financial timeline.',
    icon: <FaMoneyBillWave className="w-12 h-12 text-accent mx-auto mb-4" />
  },
  {
    title: 'ROI Calculation',
    desc: 'Calculate potential rental yields and capital appreciation for informed decisions.',
    icon: <FaChartLine className="w-12 h-12 text-accent mx-auto mb-4" />
  },
  {
    title: 'Legal Compliance',
    desc: 'Ensure all transactions comply with Dubai real estate regulations and RERA guidelines.',
    icon: <FaBalanceScale className="w-12 h-12 text-accent mx-auto mb-4" />
  }
];

const whyBuyWithUs = [
  {
    title: 'Expert Guidance',
    desc: '15+ years of Dubai real estate expertise with deep market knowledge.',
    icon: <FaUserTie className="w-10 h-10 text-accent flex-shrink-0" />
  },
  {
    title: 'Exclusive Access',
    desc: 'Early access to off-plan launches and VIP developer relationships.',
    icon: <FaKey className="w-10 h-10 text-accent flex-shrink-0" />
  },
  {
    title: 'Full Service',
    desc: 'End-to-end support from property selection to handover and beyond.',
    icon: <FaHandshake className="w-10 h-10 text-accent flex-shrink-0" />
  },
  {
    title: 'Market Insights',
    desc: 'Data-driven recommendations backed by comprehensive market analysis.',
    icon: <FaChartPie className="w-10 h-10 text-accent flex-shrink-0" />
  }
];

function BuyPage() {
  const [selectedProperty, setSelectedProperty] = useState(null);

  return (
    <div className="flex flex-col min-h-screen font-sans bg-white">
      <Navbar />

      <main className="flex-1">
        {/* Hero Section */}
        <section className="relative w-full min-h-screen flex items-center justify-center overflow-hidden">
          <video
            className="absolute inset-0 w-full h-full object-cover z-0"
            autoPlay
            loop
            muted
            playsInline
            preload="metadata"
          >
            <source src={heroVideo} type="video/mp4" />
            Your browser does not support the video tag.
          </video>
          <div className="absolute inset-0 bg-black/60 z-0" />
          <div className="relative z-10 max-w-4xl mx-auto text-center px-4 py-16">
            <h1 className="text-4xl md:text-6xl font-light text-white mb-6 drop-shadow-lg tracking-tight">
              Find Your Dream Home in Dubai
            </h1>
            <p className="text-xl text-white/90 mb-8 drop-shadow max-w-3xl mx-auto leading-relaxed font-sans">
              Explore a curated selection of luxury villas, townhouses, and apartments across Dubai's most prestigious communities. Whether you're a first-time buyer or a seasoned investor, Alpha Ahead offers handpicked properties that match your lifestyle and investment goals.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a href="#featured" className="px-8 py-4 rounded-lg bg-accent text-white font-medium text-lg hover:bg-accent/90 transition-colors duration-200 font-sans">
                View Properties
              </a>
              <a href="https://wa.me/971505606877?text=I need expert advice on buying a property in Dubai" target="_blank" rel="noopener noreferrer" className="px-8 py-4 rounded-lg bg-transparent text-white font-medium text-lg border-2 border-white/40 hover:bg-white hover:text-primary transition-colors duration-200 font-sans">
                Get Expert Advice
              </a>
            </div>
          </div>
        </section>

        {/* Featured Listings */}
        <section id="featured" className="max-w-6xl mx-auto py-20 px-4">
          <h2 className="text-3xl md:text-4xl font-light text-gray-900 mb-12 text-center tracking-tight">
            Featured Properties
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {featuredListings.map((property) => (
              <div key={property.id} className="bg-white rounded-2xl shadow-lg border border-gray-200 overflow-hidden hover:shadow-2xl transition-shadow duration-300">
                <div className="relative h-64">
                  <img
                    src={property.img}
                    alt={property.title}
                    className="w-full h-full object-cover"
                    loading="lazy"
                  />
                  <div className="absolute top-4 left-4 bg-accent text-white px-3 py-1 rounded-full text-sm font-medium font-sans">
                    {property.type}
                  </div>
                  <div className="absolute top-4 right-4 bg-black/70 text-white px-3 py-1 rounded-full text-sm font-medium font-sans">
                    {property.price}
                  </div>
                </div>
                <div className="p-6">
                  <h3 className="text-xl font-light text-gray-900 mb-2 font-sans">{property.title}</h3>
                  <p className="text-gray-600 mb-4 font-sans">{property.location}</p>
                  <div className="flex justify-between text-gray-500 text-sm mb-4 font-sans">
                    <span>{property.beds} Beds</span>
                    <span>{property.baths} Baths</span>
                    <span>{property.sqft} sq.ft</span>
                  </div>
                  <a
                    href={`https://wa.me/971505606877?text=I'm interested in booking ${property.title}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-full bg-[#25D366] text-white py-3 rounded-lg font-medium hover:bg-[#128C7E] transition-colors duration-200 font-sans text-center flex items-center justify-center gap-2"

                  >
                    <FaWhatsapp className="w-5 h-5" />
                    Contact us for booking
                  </a>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Popular Communities */}
        <section className="max-w-6xl mx-auto py-20 px-4">
          <h2 className="text-3xl md:text-4xl font-light text-gray-900 mb-12 text-center tracking-tight">
            Popular Communities
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-8">
            {popularCommunities.map((community, idx) => (
              <div key={community.name} className="relative rounded-2xl overflow-hidden group cursor-pointer">
                <img
                  src={community.img}
                  alt={community.name}
                  className="w-full h-48 object-cover transition-transform duration-300 group-hover:scale-110"
                  loading="lazy"
                />
                <div className="absolute inset-0 bg-black/50 group-hover:bg-black/70 transition-colors duration-300" />
                <div className="absolute inset-0 flex flex-col justify-end p-6">
                  <h3 className="text-xl font-light text-white mb-1 font-sans">{community.name}</h3>
                  <p className="text-white/80 text-sm font-sans">{community.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Investment Tips */}
        <section className="max-w-6xl mx-auto py-20 px-4">
          <h2 className="text-3xl md:text-4xl font-light text-gray-900 mb-12 text-center">
            Investment Tips
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {investmentTips.map((tip, idx) => (
              <div key={idx} className="bg-white rounded-2xl p-8 border border-gray-100 text-center hover:shadow-xl transition-all duration-300 hover:-translate-y-1">
                <div className="mb-4">{tip.icon}</div>
                <h3 className="text-xl font-medium text-gray-900 mb-3 font-sans">{tip.title}</h3>
                <p className="text-gray-600 text-base leading-relaxed font-sans">{tip.desc}</p>
              </div>
            ))}
          </div>
        </section>

        {/* Why Buy with Alpha Ahead */}
        <section className="max-w-6xl mx-auto py-20 px-4">
          <h2 className="text-3xl md:text-4xl font-light text-gray-900 mb-12 text-center">
            Why Buy with Alpha Ahead?
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {whyBuyWithUs.map((reason, idx) => (
              <div key={idx} className="bg-gray-50 rounded-2xl p-8 border border-gray-100 hover:shadow-lg transition-all duration-300 flex flex-col sm:flex-row items-start gap-6">
                <div className="p-4 bg-white rounded-xl shadow-sm border border-gray-100">{reason.icon}</div>
                <div>
                  <h3 className="text-xl font-medium text-gray-900 mb-2 font-sans">{reason.title}</h3>
                  <p className="text-gray-600 leading-relaxed font-sans">{reason.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* CTA Section */}
        <section id="contact" className="relative py-20 px-4">
          <div
            className="absolute inset-0 bg-cover bg-center bg-no-repeat z-0"
            style={{
              backgroundImage: `url('/property2.jpg')`,
            }}
          />
          <div className="absolute inset-0 bg-black/70 z-0" />
          <div className="relative z-10 max-w-4xl mx-auto text-center">
            <h2 className="text-3xl md:text-4xl font-light text-white mb-6">
              Ready to Find Your Dream Home?
            </h2>
            <p className="text-xl text-white/90 mb-8 max-w-2xl mx-auto">
              Let our expert team guide you through the buying process and help you make the best investment decision.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a href="https://wa.me/971505606877?text=I would like to schedule a consultation" target="_blank" rel="noopener noreferrer" className="px-8 py-4 rounded-lg bg-accent text-white font-semibold text-lg hover:bg-accent/90 transition-colors duration-200">
                Schedule Consultation
              </a>
              <div className="flex flex-col sm:flex-row gap-4">
                <a href="tel:+971505606877" className="px-8 py-4 rounded-lg bg-transparent text-white font-semibold text-lg border-2 border-white/40 hover:bg-white hover:text-primary transition-colors duration-200">
                  Call Mobile
                </a>
                <a href="tel:+97144323080" className="px-8 py-4 rounded-lg bg-transparent text-white font-semibold text-lg border-2 border-white/40 hover:bg-white hover:text-primary transition-colors duration-200">
                  Call Landline
                </a>
              </div>
            </div>
          </div>
        </section>
      </main>

      {/* Footer */}
      <Footer />
    </div>
  );
}

export default BuyPage; 